import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/serviceProduct/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/serviceProduct/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/serviceProduct/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/serviceProduct/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/serviceProduct/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/serviceProduct/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/serviceProduct/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/serviceProduct/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/serviceProduct/UPDATE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadServiceProducts(options = {}){
  const { entityKey } = options

  return (dispatch, getState) => {
    const loadedForKeys = [...getState().serviceProducts.loadedForKeys]
    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/service_products.json', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadServiceProduct(serviceProductId, options = {}){
  const { entityKey } = options

  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = [...state.serviceProducts.loadedIds]
    const loadedForKeys = [...state.serviceProducts.loadedForKeys]

    if (serviceProductId && !loadedIds.includes(serviceProductId)) loadedIds.push(serviceProductId)
    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/service_products/${serviceProductId}.json`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createServiceProduct(serviceProduct, options = {}){
  const config = {
    method: 'POST',
    data: JSON.stringify({
      service_product: serviceProduct,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/service_products.json', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function duplicateServiceProduct(serviceProduct, options = {}){
  const config = {
    method: 'POST',
    data: JSON.stringify({
      service_product: serviceProduct,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/service_products/${serviceProduct.id}/duplicate.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateServiceProduct(serviceProduct, options){
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      service_product: serviceProduct,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/service_products/${serviceProduct.id}.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
